<template>
  <div class="flex flex-col w-full overflow-hidden h-dvh" :class="{ 'priority-modals-only': !enableModals }">
    <ModalsAuthModals
      :has-auth-error="hasAuthError"
      :has-permissions-error="hasPermissionsError"
      :hide-close="true"
      project="this content"
      content="Deck"
    />
    <LayoutPassword v-if="needsPassword" @submit="submitPassword" />
    <EditorDeckIsCloningModal class="bg-opacity-100" :showing="isDeckCloning" />
    <template v-if="deck">
      <VCollapsible collapsed-background-class="bg-white">
        <EditorDeckTopBar
          class="w-full"
          :deck="deck"
          :is-edit-mode="isEditMode"
          :is-grid-view="isGridView"
          :is-saving="isDeckSaving"
          :can-leave-edit="true"
          @click="closeMultiSelect"
          @leave-edit-scene="leaveEditAndResetScene"
        />
        <EditorDeckHeader
          class="w-full"
          :deck="deck"
          :selected-slide-ids="selectedSlideIds"
          :is-edit-mode="isEditMode"
          :is-grid-view="isGridView"
          :is-book-meeting-enabled="isBookMeetingEnabled"
          :is-internal-admin="hasRole('internal_admin')"
          :has-slides="slides.length > 0"
          @toggle-view-mode="handleToggleViewMode"
          @click="closeMultiSelect"
          @duplicate-deck="showDuplicateDeckModal = true"
          @duplicate-deck-different-company=";(duplicateToDifferentCompany = true), (showDuplicateDeckModal = true)"
          @create-slide="showAddSlideModal = $event.type"
          @create-header="isAddingSectionHeader = true"
          @import-slides="showImportSlidesModal = true"
          @open-deck-settings="showDeckSettingsModal = true"
          @open-secure-access="showSecureAccessModal = true"
          @update:deck-title="updateDeck({ id: deck.id, title: $event })"
          @archive-deck="showArchiveDeckModal = true"
          @delete-deck="tryDeleteDeck"
        >
          <template #slide-title>
            <span v-if="slide && slide.type === 'M' && isEditMode && threeDViewer.world.currentModel">
              {{ threeDViewer.world.currentModel.id }}&ensp;&ndash;&ensp;{{ threeDViewer.world.currentModel.title }}
            </span>
            <span v-if="slide && slide.type === 'S' && isEditMode">
              {{ siteData?.title }}
            </span>
          </template>
        </EditorDeckHeader>
      </VCollapsible>
      <div v-show="!isGridView" class="relative z-0 flex flex-row flex-grow overflow-y-hidden">
        <ContainerResizer enable-right-collapse>
          <template #leftSide>
            <div v-show="shouldDisplayThumbnailList" class="flex flex-col w-64 h-full bg-gray-800 max-w-72 min-w-48">
              <SearchInput v-model="slideSearch" class="text-white bg-gray-700" name="deck" />
              <div class="h-full overflow-y-auto">
                <PortalTarget name="deck-editor-thumbnail-slides" />
              </div>
              <EditorDeckSidebarNavButtons
                class="sidebar-transition"
                :current-slide-index="slides.findIndex(s => s.id === selectedSlideIds[0])"
                :first-valid-slide="firstValidSlide"
                :last-valid-slide="lastValidSlide"
                :disabled="slides.length !== filteredSlides.length || selectedSlideIds.length !== 1"
                @back="back"
                @next="next"
              />
            </div>
          </template>
          <template #content>
            <div class="relative flex-grow h-full">
              <PortalTarget name="deck-editor-notification" />
              <EditorDeckSlidesMultipleSelectedView
                v-if="showSelectedSlides && !isCreatingMultipleNewSiteSlides"
                :slides="selectedSlides"
                @on-replace=";(slidesToReplace = $event), (showAddSlideModal = 'P')"
                @on-export="handleMultipleSlidesSelectedViewExport"
                @on-delete=";(slidesToDelete = $event), (showDeleteSlideModal = true)"
                @on-close="closeMultiSelect"
                @update-title-visibility="updateSlideTitleVisibility"
              />
              <EditorDeckSlidesMultiSiteEditor
                v-else-if="isCreatingMultipleNewSiteSlides"
                :selected-slides="selectedSlides"
                :three-d-viewer="threeDViewer"
                @update:slide="updateSlide"
                @exit="exitMultiSiteEditor"
              />
              <EditorDeckSlidesDefaultView
                v-else-if="!slide"
                :is-internal-admin="hasRole('internal_admin')"
                @create-slide="showAddSlideModal = $event"
              />
              <EditorDeckSlidesMeetingEditorSlide
                v-else-if="slide?.type === 'E'"
                v-model:show-meeting-slide-top-bar="showMeetingSlideTopBar"
                :slide="slide"
                :request-settings="requestSettings"
                :company-id="deck.company.id"
                @update:slide="updateBAMSlide"
                @activate-book-a-meeting="activateBAM"
              />
              <EditorDeckSlidesImageEditorSlide
                v-else-if="slide?.type === 'I'"
                v-model:showImageReplace="showImageReplace"
                :slide="slide"
                :company-id="deck.company.id"
                :folder-id="shelf.images"
                @update:slide="updateSlide"
                @error="onError"
              />
              <EditorDeckSlidesSiteEditorSlide
                v-else-if="slide?.type === 'S'"
                v-model:siteData="siteData"
                :site="site"
                :slide="slide"
                :slide-refresh-key="slideRefreshKey"
                :camera-id="slide?.site_id === site.siteId ? slide.camera_id : undefined"
                :is-editing="isEditingSite"
                :is-new="isNewSiteSlide"
                :page-locked="pageLocked"
                :three-d-viewer="threeDViewer"
                @contextmenu="open($event)"
                @camera-ready="isSlideLoading = !$event"
                @update:slide="updateSiteSlide"
                @create-slide="insertSlides($event, slide.order + 1).then(() => (isNewSiteSlide = true))"
                @finish-slide="finalizeSiteSlide"
                @leave-edit-and-reset="leaveEditAndResetScene"
                @start-edit="isEditingSite = true"
                @change="lockPage"
              />
              <EditorDeckSlidesThreeDEditor
                v-else-if="slide.type === 'M'"
                v-model:edit-mode="threeDEditMode"
                :loading="threeDViewer.progress.value < 1"
                :slide="slide"
                :uuid="uuid"
                :deck="deck"
                :is-composing-new-scene="isComposingNewScene"
                :is-duplicating-scene="isDuplicatingScene"
                :is-saving-as-scene="isSavingAsScene"
                :newly-created-slide-id="newlyCreatedSlideId"
                :context-menu="contextMenu"
                :three-d-viewer-error="threeDViewer.error.value"
                :three-d-viewer="threeDViewer"
                @update:slide="updateSlide"
                @contextmenu="open"
                @update:slide-title="updateSlide"
                @update-text-scale="threeDViewer.threeDRepo?.updateTextScale"
                @leave-edit-scene="leaveEditAndResetScene"
                @slide-loading-state="onSlideLoading"
                @save="saveThreeDSlide"
                @save-as="saveAsThreeDSlide"
                @blur-title="blurTitle"
                @change="lockPage"
                @add-drill-data-to-model="showDrillLocalizationModal = true"
                @change-model-drill-data-localization="handleChangeModelDrillDataLocalization"
                @remove-drill-data-from-model="void 0"
                @select-drill-data="drillDataSet = $event"
                @add-drill-data-v1-model="showDataSetV1Modal = true"
                @notify="displayNotificationToast"
              />
              <EditorDeckSlidesPdfEditorSlide
                v-else-if="slide?.type === 'P'"
                v-model:show-pdf-replace="showPdfReplace"
                :slide="slide"
                :folder-id="shelf.pdfs"
                :company-id="deck.company_id"
                :slide-refresh-key="slideRefreshKey"
                @update:slide="updateSlide"
                @refetch-file="refreshDeck"
              />
              <EditorDeckSlidesVideoEditorSlide
                v-else-if="slide.type === 'V'"
                v-model:show-video-replace="showVideoReplace"
                :slide="slide"
                :folder-id="shelf.videos"
                :company-id="deck.company_id"
                :is-internal-admin="hasRole('internal_admin')"
                @update:slide="updateSlide"
                @error="onError"
              />
              <EditorDeckSlidesUrlEditor
                v-else-if="slide?.type === 'D'"
                :slide="slide"
                @update:slide="updateSlide"
                @notify="displayNotificationToast"
              />
              <h2 v-else>{{ slide.title }}</h2>
              <DeckCaptionEditable
                v-if="shouldShowCaptions"
                class="z-20"
                :slide="slide"
                data-intercom-target="slide-caption"
                @save="updateCaption"
              />
            </div>
          </template>
          <template #rightSide>
            <div class="relative z-10 flex flex-col h-full resize-x right-panel w-96 max-w-108 min-w-64">
              <PortalTarget name="editor-details" />
            </div>
          </template>
        </ContainerResizer>
      </div>
      <EditorDeckGridView v-show="isGridView" />
      <Portal :to="isGridView ? 'deck-grid-view' : 'deck-editor-thumbnail-slides'">
        <EditorDeckThumbnailSlides
          v-show="filteredSlides.length || isAddingSectionHeader"
          class="flex-1 h-full sidebar-transition"
          :deck="deck"
          :slides="filteredSlides"
          :full-slide-list-count="slides.length"
          :selected-slide-ids="selectedSlideIds"
          :analytics-event-name="analyticsEventName"
          :user="userState.user"
          :is-adding-section-header="isAddingSectionHeader"
          :request-settings="requestSettings"
          :is-grid-view="isGridView"
          @set-current-slide="setSelectedSlideId"
          @reorder-slides="reorderSlides"
          @update:slide="updateSlide"
          @update-title-visibility="updateSlideTitleVisibility"
          @contextmenu="open"
          @delete-slides="confirmDeleteSlides"
          @create-header-slide="generateHeaderSlide"
          @cancel-create-header-slide="isAddingSectionHeader = false"
          @duplicate-slides="duplicateSlidesAndSelect"
          @copy-link-to-slide="copyLinkToSlide"
          @download-three-d-slide="downloadThreeDSlide"
          @click.shift="onShiftClick($event.payload)"
          @click.meta="onCtrlClick($event.payload)"
          @click.ctrl="onCtrlClick($event.payload)"
          @shift-click:title="onShiftClick"
          @ctrl-click:title="onCtrlClick"
          @export-slides="slidesToExport = $event"
          @replace-pdf-source="showPdfReplace = true"
          @multi-select="multiSelectSlidesByType"
          @blur:title="updateNewlyCreatedSlideId(null)"
          @bulk-replace-pdf-source=";(slidesToReplace = $event), (showAddSlideModal = 'P')"
        />
        <EditorThumbnailListEmptyState v-show="!filteredSlides.length && !isAddingSectionHeader" />
      </Portal>
      <Portal :to="isGridView ? 'deck-grid-view-notification' : 'deck-editor-notification'">
        <VNotification
          :key="JSON.stringify(notification)"
          class="absolute right-4 w-96 top-16"
          :show="!!notification"
          :delay="notification && notification.action ? 0 : 4000"
          :variant="notification && notification.action ? 'with-button' : ''"
          :type="notification && notification.type"
          :title="notification && notification.title ? notification.title : ''"
          @dismissed="displayNotificationToast"
        >
          <template #title>{{ notification && notification.title }}</template>
          <template v-if="notification && (notification.text || notification.action)" #default>
            <p v-if="notification.text" class="mb-0">{{ notification.text }}</p>
            <template v-if="notification.action">
              <VButton
                class="w-full flex items-center justify-center mt-3 gap-1.5"
                size="md"
                variant="copper"
                @click="notification.action.onClick"
              >
                <DynamicHeroIcon class="w-5" :icon-name="notification.action.icon" icon-style="solid" />
                {{ notification.action.name }}
              </VButton>
            </template>
          </template>
        </VNotification>
      </Portal>
      <VModal class="pointer-events-auto" :showing="showThreeDResourceErrorModal" hide-close="hide-close">
        <div class="max-w-md text-sm text-center">
          <p class="mt-1 text-lg font-medium">Failed to load 3D model</p>
          <p class="text-gray-600 my-4">Please refresh your browser and try again.</p>
          <VButton class="w-full" variant="copper" @click="onThreeDResourceErrorModalRefresh">Refresh</VButton>
        </div>
      </VModal>
      <EditorDeckModalsAddThreeDSlide
        v-if="showAddSlideModal === 'M'"
        :deck="deck"
        :showing="showAddSlideModal === 'M'"
        @add-slide="handleAddThreeDSlide"
        @error="onError($event), (showAddSlideModal = null)"
        @update:showing="showAddSlideModal = $event"
        @copy-slides="copySlides($event).then(() => (showAddSlideModal = null))"
      />
      <EditorDeckModalsAddSiteSlide
        v-if="showAddSlideModal === 'S'"
        :deck="deck"
        :showing="showAddSlideModal === 'S'"
        @add-slide="insertSlides($event, lastSelectedOrder + 1).then(() => activateNewSiteSlide())"
        @error="onError($event), (showAddSlideModal = null)"
        @update:showing="showAddSlideModal = $event"
      />
      <EditorDeckModalsAddPdfSlide
        v-if="showAddSlideModal === 'P'"
        :deck="deck"
        :folder-id="shelf.pdfs"
        :showing="showAddSlideModal === 'P'"
        :slides-to-replace="slidesToReplace"
        @add-slides="handleAddSlide"
        @bulk-replace-pdf-source="bulkUpdateSlides"
        @update:showing=";(showAddSlideModal = $event), (slidesToReplace = null)"
      />
      <EditorDeckModalsAddImageSlide
        v-if="showAddSlideModal === 'I'"
        :deck="deck"
        :folder-id="shelf.images"
        :showing="showAddSlideModal === 'I'"
        @add-slide="handleAddSlide"
        @update:showing="showAddSlideModal = $event"
      />
      <EditorDeckModalsAddVideoSlide
        v-if="showAddSlideModal === 'V'"
        :showing="showAddSlideModal === 'V'"
        :folder-id="shelf.videos"
        :deck="deck"
        @add-slide="handleAddSlide"
        @update:showing="showAddSlideModal = $event"
      />
      <EditorDeckModalsAddUrlSlide
        v-if="showAddSlideModal === 'D'"
        :showing="showAddSlideModal === 'D'"
        :deck="deck"
        @add-slide="handleAddSlide"
        @update:showing="showAddSlideModal = $event"
      />
      <EditorDeckModalsDeleteSlide
        v-if="slidesToDelete || slide"
        :slides="(slidesToDelete as Slide[]) || [slide]"
        :showing="showDeleteSlideModal"
        @update:showing="showDeleteSlideModal = $event"
        @delete-slides=";(showDeleteSlideModal = false), tryDeleteSlides($event)"
      />
      <EditorDeckModalsDeleteDeck
        v-if="deleteDeckWarning.showing"
        :deck="deck"
        :showing="deleteDeckWarning.showing"
        @update:showing="deleteDeckWarning.cancel"
        @delete-deck="deleteDeckWarning.confirm"
      />
      <EditorDeckModalsDeleteLabels
        v-if="deleteLabelsConfirmation.showing"
        :showing="deleteLabelsConfirmation.showing"
        @update:showing="deleteLabelsConfirmation.showing = $event"
        @confirm-delete="deleteLabelsConfirmation.onComplete"
      />
      <EditorDeckModalsEmbedDeleteSlideConfirmation
        v-if="slidesToDelete"
        :slides="slidesToDelete"
        :showing="showEmbedDeleteConfirmationModal"
        :deck-embeds="deckEmbeds"
        @update:showing="showEmbedDeleteConfirmationModal = $event"
        @delete-slides=";(showEmbedDeleteConfirmationModal = false), (slidesToDelete = null), deleteSlides($event)"
      />
      <EditorDeckModalsEmbedDeleteDeckConfirmation
        v-if="deleteDeckWarningEmbeds.showing"
        :showing="deleteDeckWarningEmbeds.showing"
        :deck-embeds="deckEmbeds"
        @update:showing="deleteDeckWarningEmbeds.cancel"
        @delete-deck="deleteDeckWarningEmbeds.confirm"
      />
      <EditorDeckModalsDeckSettings
        v-if="showDeckSettingsModal"
        :deck="deck"
        :slides="slides"
        :drill-data-set-manager="threeDViewer.world.drillDataSetManager!"
        :showing="showDeckSettingsModal"
        :request-settings="requestSettings"
        :is-deck-saving="isDeckSaving"
        @update:showing="showDeckSettingsModal = $event"
        @activate-book-a-meeting="activateBAM"
        @update:deck="handleUpdateDeckSettings"
      />
      <EditorDeckModalsDeckExportSelector
        v-if="slidesToExport && slidesToExport.length"
        :deck="deck"
        :showing="!!slidesToExport"
        :slides-to-export="slidesToExport"
        :company-id="deck.company.id"
        @export="exportSlides($event)"
        @close="slidesToExport = null"
        @cancel="slidesToExport = null"
      />
      <EditorDeckModalsSecureAccessManagement
        v-if="showSecureAccessModal"
        :deck="deck"
        :showing="showSecureAccessModal"
        @update:showing="showSecureAccessModal = $event"
        @update:deck-internal-access="updateDeckInternalAccessNoNotification($event)"
      />
      <EditorDeckModalsBookMeeting
        v-if="showAddSlideModal === 'E' && requestSettings"
        :deck="deck"
        :request-settings="requestSettings"
        :showing="showAddSlideModal === 'E'"
        @update:showing="showAddSlideModal = $event"
        @activate-book-a-meeting="activateBookMeeting"
        @add-meeting-slide="handleAddSlide"
      />
      <EditorDeckModalsArchiveDeck v-model:showing="showArchiveDeckModal" :deck="deck" @archive="handleArchiveDeck" />
      <EditorDeckModalsDuplicateDeck
        v-if="showDuplicateDeckModal"
        :showing="showDuplicateDeckModal"
        :deck="deck"
        :user="userState.user"
        :duplicate-to-different-company="duplicateToDifferentCompany"
        :three-d-viewer="threeDViewer"
        :priority-cameras="priorityCameras"
        @update:showing=";(showDuplicateDeckModal = $event), (duplicateToDifferentCompany = $event)"
        @duplicate-deck="(...args: any[]) => duplicateDeck(...(args as [any, any]))"
      />
      <EditorDeckModalsSceneDoesNotExist
        v-if="slideWithoutScene"
        :showing="!!slideWithoutScene"
        :slide="slideWithoutScene"
        @update:showing="slideWithoutScene = null"
        @delete-slide=";(slideWithoutScene = null), deleteSlides([$event])"
      />
      <EditorDeckModalsImportSlide
        v-if="showImportSlidesModal"
        :deck="deck"
        :showing="showImportSlidesModal"
        @update:showing="showImportSlidesModal = false"
        @copy-slides="copySlides($event.slides).then(() => onImportSlides($event))"
      />
      <EditorDeckModalsDrillLocalization
        v-if="showDrillLocalizationModal && (drillDataSet || initialDrillDataSetLayerGroup)"
        :deck="deck"
        :showing="showDrillLocalizationModal"
        :drill-data-set="drillDataSet"
        :initial-drill-data-set-layer-group="initialDrillDataSetLayerGroup"
        :drill-data-set-manager="threeDViewer.world.drillDataSetManager!"
        :is-internal-admin="hasRole('internal_admin')"
        @update:showing=";(showDrillLocalizationModal = false), (initialDrillDataSetLayerGroup = null)"
        @notify="displayNotificationToast($event)"
      />
      <EditorDeckModalsDatasetV1Model
        v-if="showDataSetV1Modal"
        :showing="showDataSetV1Modal"
        @update:showing="showDataSetV1Modal = false"
      />
      <PortalTarget name="editor-modals" />
    </template>
    <VContextMenu class="z-50" :context-menu="contextMenu" @close="close" />
  </div>
</template>

<script lang="ts" setup>
import { findLast, pipe, slice } from 'ramda'
import { Portal, PortalTarget } from 'portal-vue'
import { debounce } from '@/lib/utils'
import { UseEditDeckAuthError, UseEditDeckPermissionsError, UseEditDeckPasswordRequiredError } from '@/composables/useEditDeck'
import type { Site } from '~/entities/site'
import type { DeckEmbed } from '~/entities/embed'
import type { Deck, SiteSlide, Slide, SlideType } from '~/entities/deck'
import type { Drill } from '~/entities/drill'
import { ShelfFolders } from '~/entities/shelfFile'
import type { Folder } from '~/entities/folder'
import type {
  CameraData,
  ClusterVisibility,
  DrillLabelVisibility,
  LabelVisibility,
  NodeVisibility
} from '~/entities/model3d'
import type { ScheduleSetting } from '~/entities/meeting'
import { isThreeDSlide } from '~/lib/type-utils'
import type { DeepReadonly } from 'vue'
import { pluralize } from '~/lib/pluralize'
import type { ThreeDEditMode } from '~/entities/model3d'

const props = defineProps({
  deckId: {
    type: String,
    required: true,
  },
  initSlide: {
    type: String,
    default: null,
  },
  uuid: {
    type: String,
    default: null,
  },
})

const { $analytics, $sentry } = useNuxtApp()
const { showAlert, hideAlert } = useAlerts()
const globalFetchHeaders = useState<{ [key: string]: string }>('global-fetch-headers', () => ({}))
const route = useRoute()
const { state: userState, hasRole } = useUser()
const slideSearch = ref('')
const isSlideLoading = ref(true)
const initialSlideId: Ref<number | null> = ref(props.initSlide ? parseInt(props.initSlide, 10) : null)
const selectedSlides = computed(() => filteredSlides.value.filter(slide => selectedSlideIds.value.includes(slide.id)))
const shouldDisplayThumbnailList = computed(() => slides.value.length)
const isAddingSectionHeader = ref(false)
const enableModals = ref(true)
const isNewSiteSlide = ref(false)
const isGridView = ref(false)
const siteData = ref<Site | null>(null)
const deleteDeckWarning = reactive<{ showing: boolean; cancel(): void; confirm(): void }>({
  showing: false,
  cancel: () => {},
  confirm: () => {},
})
const showThreeDResourceErrorModal = ref<boolean>(false)
const threeDResourceManagerErrorMessage = ref<string>('')
const deleteDeckWarningEmbeds = reactive<{ showing: boolean; cancel(): void; confirm(): void }>({
  showing: false,
  cancel: () => {},
  confirm: () => {},
})
const threeDEditMode = ref<ThreeDEditMode>('read')
const isEditingSite = ref(false)
const isEditMode = computed(() => threeDEditMode.value === 'write' || !!isEditingSite.value)
const isComposingNewScene = ref(false)
const isSavingAsScene = ref(false)
const isDuplicatingScene = ref(false)
const showPdfReplace = ref(false)
const showImageReplace = ref(false)
const showVideoReplace = ref(false)
const showMeetingSlideTopBar = ref(false)
const deckEmbeds = ref<DeckEmbed[]>([])

const notification: Ref<GlobalNotification | null> = ref(null)
const slide = computed(() => {
  if (isGridView.value) return null

  return selectedSlideIds.value.length === 1 ? slides.value.find(s => s.id === selectedSlideIds.value[0]) : null
})

const slideWithoutScene = ref<Slide | null>(null)
const slidesToDelete = ref<Slide[] | null>(null)
const slidesToExport = ref<Slide[] | null>(null)
const slidesToReplace = ref<Slide[] | null>(null)
const analyticsEventName = ref('deckEditor')
const showAddSlideModal = ref<SlideType | null>(null)
const showSettingsModal = ref(false)
const showDeleteSlideModal = ref(false)
const showEmbedDeleteConfirmationModal = ref(false)
const showDeckSettingsModal = ref(false)
const showSecureAccessModal = ref(false)
const showDuplicateDeckModal = ref(false)
const showImportSlidesModal = ref(false)
const showDrillLocalizationModal = ref(false)
const showDataSetV1Modal = ref(false)
const duplicateToDifferentCompany = ref(false)
const deleteLabelsConfirmation = ref({ showing: false, onComplete: (_confirmed: boolean) => {} })

const modelId: Ref<string> = ref('')
const cameraId: Ref<number | undefined> = ref(undefined)
const drillDataSet: Ref<Drill | undefined> = ref(undefined)

const { copyToClipboard } = useCopyToClipboard()

let layerChangeDebounces: Record<string, ReturnType<typeof setTimeout>> = {}
let changedLayers: Record<string, boolean> = {}

const priorityCameras = ref<CameraData[]>([])
const threeDViewer = useThreeDViewer({
  accessType: 'edit',
  priorityCameras,
  onConfirmLabelsDelete: () =>
    new Promise(resolve => {
      deleteLabelsConfirmation.value = {
        showing: true,
        onComplete: confirmed => {
          resolve(confirmed)
          deleteLabelsConfirmation.value = {
            showing: false,
            onComplete: (_confirmed: boolean) => {},
          }
        },
      }
    }),
  onSaved: eventName => {
    const nodeVisibilityToggledOn = Object.entries(changedLayers).filter(([_, value]) => value).length
    const nodeVisibilityToggledOff = Math.abs(Object.keys(changedLayers).length - nodeVisibilityToggledOn)
    $analytics.track(eventName, {
      nodeVisibilityToggledOn,
      nodeVisibilityToggledOff,
      deckName: deck.value?.title,
      deckId: deck.value?.id,
      slideId: slide.value?.id,
    })
    changedLayers = {}
  },
  onSetScene() {
    changedLayers = {}
  },
  onSetLayerVisibility(
    param: Array<NodeVisibility & LabelVisibility & DrillLabelVisibility & ClusterVisibility>,
    source: 'user' | 'system',
  ) {
    if (source !== 'user') return
    for (let item of param) {
      const id = item.nodeId || item.labelId || item.drillLabelId || item.clusterId
      clearTimeout(layerChangeDebounces[id])
      // TODO: Fix click and drag functionality triggering multiple events
      layerChangeDebounces[id] = setTimeout(() => {
        if (id in changedLayers) {
          delete changedLayers[id]
        } else {
          changedLayers[id] = item.visible
        }
      }, 10)
    }
  },
})

function onThreeDResourceErrorModalRefresh() {
  $analytics?.track('modelViewerErrorRefresh', {
    userId: props.uuid,
    deckId: deck.value?.id,
    modelId: slide.value?.model_id,
  })
  location.reload()
}

watch(
  () => threeDViewer.error.value,
  error => {
    threeDResourceManagerErrorMessage.value = error ?? ''
    showThreeDResourceErrorModal.value = threeDResourceManagerErrorMessage.value.length > 0
  },
  { immediate: true },
)

const initialDrillDataSetLayerGroup = ref<DrillDataSetLayerGroup | null>(null)

const handleChangeModelDrillDataLocalization = (dataSet: DrillDataSetLayerGroup) => {
  initialDrillDataSetLayerGroup.value = dataSet
  showDrillLocalizationModal.value = true
}

const useEditDeckResult = useEditDeck({
  id: toRef(props, 'deckId'),
  user: computed(() => userState.value.user),
  initialSlideId,
  notify(notificationOpts: GlobalNotification) {
    notification.value = notificationOpts
  },
  onError,
  leaveEditScene,
  setSelectedSlideIds,
  isSavingAsScene,
  threeDViewer,
})

const {
  error,
  deck,
  slides,
  bulkUpdateSlides,
  cloning: isDeckCloning,
  currentSlideIndex,
  deleteDeck,
  deleteSlides,
  duplicateDeck,
  duplicateSlides,
  exportAndDuplicateSlides,
  firstValidSlide,
  getEmbeds,
  isSelectableSlide,
  lastValidSlide,
  reorderSlides: _reorderSlides,
  refreshDeck,
  saving: isDeckSaving,
  site,
  slideRefreshKey,
  updateDeck,
  updateSlide,
  updateCaption: _updateCaption,
  newlyCreatedSlideId,
  updateNewlyCreatedSlideId,
  saveThreeDSlide,
  saveAsThreeDSlide,
} = useEditDeckResult

useDeckSetActiveThreeDScene({
  slides,
  currentSlideIndex,
  slideRefreshKey,
  setActiveScene: threeDViewer.setActiveScene,
})

const showArchiveDeckModal = ref(false)

watch(
  () => slides.value,
  newSlides => {
    if (!newSlides.length) {
      isGridView.value = false
    }
  },
)

// Wrap this function and curry some local refs
async function exportSlides({ deck, duplicateModelOnExport }: { deck: Deck; duplicateModelOnExport: boolean }) {
  if (!slidesToExport.value) return

  showDeckExportSelectorModal.value = true

  if (duplicateModelOnExport) {
    await exportAndDuplicateSlides(deck, slidesToExport.value)
  } else {
    await useEditDeckResult.exportSlides(deck, slidesToExport.value)
  }

  slidesToExport.value = null
}

async function reorderSlides(reorderedSlides: Pick<Slide, 'id' | 'order'>[]) {
  if (!deck.value) return
  const changed = slides.value.filter(s1 => s1.order !== reorderedSlides.find(s2 => s2.id === s1.id)?.order)

  await _reorderSlides(reorderedSlides)

  $analytics?.track('DeckEditorReorderSlidesClickDrag', {
    deckName: deck.value?.title,
    deckId: deck.value?.id,
    slideType: changed.length === 1 ? changed[0].type : null,
    bulkAction: changed.length > 1,
  })
}

async function updateSiteSlide(partialSlide: Partial<SiteSlide>) {
  await updateSlide(partialSlide)
  isNewSiteSlide.value = false

  $analytics?.track('DeckEditor360SceneEditorClickSave', {
    deckName: deck.value?.title,
    deckId: deck.value?.id,
    projectId: (<SiteSlide>slide.value)?.project_id,
    siteId: partialSlide.site_id,
  })
}

const { locked: pageLocked, lock: lockPage, unlock: unlockPage } = useLockedBoolean()

const { results: filteredSlides, setSearch } = useObjectSearch<EditorSlide>({
  objects: readonly(slides),
  searchProperty: 'title',
  compFn: (p, n) => JSON.stringify(p) === JSON.stringify(n),
})

watch(
  slideSearch,
  debounce(() => setSearch(slideSearch.value), 50),
)

const {
  selectedSlideIds,
  setSelectedSlideIds: _setSelectedSlideIds,
  onCtrlClick: _onCtrlClick,
  onShiftClick: _onShiftClick,
} = useMultipleSlidesSelect({
  slides: filteredSlides,
  currentSlideId: computed(() => slides.value[useEditDeckResult.currentSlideIndex.value]?.id as number),
  setCurrentSlideIndex: id => useEditDeckResult.setCurrentSlideIndex(slides.value.findIndex(s => id === s.id) as number),
})

function onCtrlClick(id: number) {
  if (isNewSiteSlide.value) {
    setSelectedSlideIds([id])
    return
  }
  _onCtrlClick(id)
}

function onShiftClick(id: number) {
  if (isNewSiteSlide.value) {
    setSelectedSlideIds([id])
    return
  }
  _onShiftClick(id)
}

const { isSceneLoading } = useThreeDSceneLoading({ progress: threeDViewer.progress })

type ShelfType = Partial<Record<Lowercase<ShelfFolders>, number>>
const shelf = ref<ShelfType>({})
watch(
  deck,
  async (deck, oldDeck) => {
    if (!deck) return
    const { results: folders } = await $fetch<{ results: Folder[] }>(
      `v2/companies/${deck.company_id}/shelf/folders?parent_id=null`,
      useFetchSharedOptions(),
    )
    const images = folders.find(folder => folder.name.includes(ShelfFolders.Images))?.id
    const pdfs = folders.find(folder => folder.name.includes(ShelfFolders.PDFs))?.id
    const videos = folders.find(folder => folder.name.includes(ShelfFolders.Videos))?.id

    shelf.value = { images, pdfs, videos }

    const { results } = await $fetch<{ results: CameraData[] }>(`/v2/decks/${deck.id}/priority_cameras?limit=5000`, useFetchSharedOptions())
    priorityCameras.value = results

    if (deck && !oldDeck) {
      $analytics?.track('DeckEditorPageLoad', {
        deckId: deck.id,
        deckName: deck.title,
        deckAccess: deck.view_access_type,
        deckType: deck.type_tags[0] ?? 'None',
      })
    }
  },
  { immediate: true },
)

watchEffect(() => {
  // Turn this on/off here instead of within the handler for performance reasons
  // see https://developer.chrome.com/blog/page-lifecycle-api/#the-beforeunload-event
  if (pageLocked.value) {
    window.addEventListener('beforeunload', _unloadHandler, { capture: true })
  } else {
    window.removeEventListener('beforeunload', _unloadHandler, { capture: true })
  }
})

function _unloadHandler(e: Event) {
  if (!isEditMode.value) return
  e.preventDefault()
  // This is required to have it work in Chrome. The message will be ignored.
  // @ts-ignore
  return (e.returnValue = 'Are you sure you want to exit? Changes to this scene will not be saved.')
}

// Keep initial slide synced to current slide so refetching the deck doesn't hop us back to the first slide
watch(
  [useEditDeckResult.currentSlideIndex, slides],
  ([index, slides]) => {
    const slide = slides[index]
    if (slide) {
      initialSlideId.value = slide.id

      if (slide.type !== 'M') {
        isSlideLoading.value = false
      }
    }
  },
  { immediate: true },
)

watch(useEditDeckResult.currentSlideIndex, () => {
  isEditingSite.value = false
})

watchEffect(() => {
  globalFetchHeaders.value['X-Deck-Id'] = deck.value?.id ? `${deck.value?.id}` : ''
})

const handleClearMultiSelect = (event: KeyboardEvent) => {
  if (selectedSlides.value.length > 1 && !showDeckExportSelectorModal.value && event.key === 'Escape') {
    closeMultiSelect()
  }
}

const showDeckExportSelectorModal = ref<boolean>(false)

const handleMultipleSlidesSelectedViewExport = (event: Slide[]) => {
  slidesToExport.value = event
  showDeckExportSelectorModal.value = true
}

const handleSlideKeyControls = (e: KeyboardEvent) => {
  const element = e.target as HTMLElement
  const isInputOrTextArea = ['INPUT', 'TEXTAREA'].includes(element.tagName)
  const isContentEditable = element.getAttribute('contenteditable')

  if (isInputOrTextArea || isContentEditable) return

  if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
    e.preventDefault()
    element.blur()
    back()
  } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
    e.preventDefault()
    element.blur()
    next()
  }
}

onMounted(() => {
  document.addEventListener('keyup', handleClearMultiSelect)
  document.addEventListener('keydown', handleSlideKeyControls)
})

onUnmounted(() => {
  delete globalFetchHeaders.value['X-Deck-Id']
  document.removeEventListener('keyup', handleClearMultiSelect)
  document.removeEventListener('keydown', handleSlideKeyControls)
})

async function submitPassword(params: { password: string }) {
  $analytics?.track('deckPasswordSubmitted', _parseAnalyticsPayload(route.params.id))
  hideAlert()

  try {
    await useEditDeckResult.submitPassword(params)
  } catch (e) {
    $analytics?.track('deckPasswordFailure', _parseAnalyticsPayload(route.params.id))

    showAlert({
      alertType: 'danger',
      alertMessage: 'Sorry, the password entered is incorrect. Please check the password and try again.',
    })
  }
}

function _parseAnalyticsPayload(identifier: any) {
  if (isNaN(parseInt(identifier))) {
    return { urlSlug: identifier }
  }

  return { deckId: parseInt(identifier) }
}

const { requestSettings, fetchRequestSettings, activateBookMeeting: _activateBookMeeting } = useBookMeeting()

async function activateBookMeeting(currentUser: ScheduleSetting) {
  try {
    await _activateBookMeeting(currentUser)
    notification.value = { title: 'Meeting booking turned on' }
  } catch (e) {
    onError(e)
  }
}

watch(
  [slide, showAddSlideModal, showDeckSettingsModal],
  async ([slide, showAddSlideModal, showDeckSettingsModal]) => {
    if ((slide?.type === 'E' || showAddSlideModal === 'E' || showDeckSettingsModal) && deck.value) {
      await fetchRequestSettings(deck.value.company_id)
    }
  },
  { immediate: true },
)

async function updateBAMSlide(slide: Partial<Slide>) {
  await updateSlide(slide)
}

async function activateBAM(currentUserSettings: ScheduleSetting) {
  await activateBookMeeting(currentUserSettings)
}

function exitMultiSiteEditor() {
  isEditingSite.value = false
  isNewSiteSlide.value = false
}

watch(
  slide,
  newSlide => {
    if (!newSlide || !isThreeDSlide(newSlide)) return

    modelId.value = `${newSlide.model_id}`
    cameraId.value = newSlide.camera_id ?? undefined
  },
  { immediate: true },
)

watch(slide, (newSlide, oldSlide) => {
  const didSlideChange = newSlide?.id !== oldSlide?.id
  const isThreeDInWriteMode = threeDEditMode.value === 'write'

  if (didSlideChange && !newlyCreatedSlideId.value && isThreeDInWriteMode && isThreeDSlide(oldSlide)) {
    leaveEditAndResetScene()
  }

  if (didSlideChange && oldSlide?.type === 'S') {
    finalizeSiteSlide()
  }
})

const showSelectedSlides = computed(
  () => selectedSlides.value.length > 1 || (selectedSlides.value[0] && !isSelectableSlide(selectedSlides.value[0])),
)

const isCreatingMultipleNewSiteSlides = computed(() => {
  const isAllSiteSlides = selectedSlides.value.every(slide => slide.type === 'S')

  return selectedSlides.value.length > 1 && isAllSiteSlides && isNewSiteSlide.value
})

const shouldShowCaptions = computed(() => slide.value && !'EVH'.includes(slide.value.type))

const lastSelectedOrder = computed(() =>
  Math.max(
    ...selectedSlideIds.value.map(id => slides.value.find(s => s.id === id)?.order).filter(Number.isInteger as (x: unknown) => x is number),
  ),
)

const generateHeaderSlide = (slide: Partial<Slide>) =>
  useEditDeckResult.generateHeaderSlide(slide).then(() => (isAddingSectionHeader.value = false))

async function duplicateSlidesAndSelect(duplicatingSlides: Slide[]) {
  const dupedSlides = await duplicateSlides(duplicatingSlides)

  if (dupedSlides.length === 1) {
    const slide = dupedSlides[0]

    if (slide.type === 'M') updateNewlyCreatedSlideId(slide.id)

    if (dupedSlides[0].type === 'M') {
      isComposingNewScene.value = true
      isDuplicatingScene.value = true
    }
  }

  if (slides.value) {
    const slideIds = (dupedSlides.length ? dupedSlides : duplicatingSlides.slice(0, 1)).map(s => s.id)
    setSelectedSlideIds(slideIds)
  }

  duplicatingSlides.forEach(slide => {
    $analytics?.track('DeckEditorClickDuplicate', {
      deckName: deck.value?.title,
      deckId: deck.value?.id,
      slideType: slide.type,
      bulkAction: duplicatingSlides.length > 1,
    })
  })
}

function closeMultiSelect() {
  const firstSlide = selectedSlides.value[0]
  if (selectedSlides.value.length > 1) setSelectedSlideId(firstSlide.id)
}

function setSelectedSlideId(id: number) {
  updateNewlyCreatedSlideId(null)

  const slide = slides.value.find(s => s.id === id) as Slide
  if (['M', 'S'].includes(slide.type) && !slide.camera_id) {
    slideWithoutScene.value = slide
    return
  }
  if (slide && isSelectableSlide(slide)) {
    setSelectedSlideIds([id])
    slidesToDelete.value = null
    slidesToExport.value = null
    slidesToReplace.value = null
  }
}

function confirmDeleteSlides(slides: Slide[]) {
  threeDEditMode.value = 'read'
  isEditingSite.value = false
  showDeleteSlideModal.value = true
  slidesToDelete.value = slides
}

// check if we have embeds before deleting so we can warn accordingly
const tryDeleteSlides = async (slides: Slide[]): Promise<void> => {
  deckEmbeds.value = await getEmbeds()
  const hasEmbeds = slides.some(slide => deckEmbeds.value?.some(de => de.start_slide_id === slide.id || de.end_slide_id === slide.id))
  if (hasEmbeds) {
    showEmbedDeleteConfirmationModal.value = true
  } else {
    slidesToDelete.value = null
    await deleteSlides(slides)
    slides.forEach(slide => {
      $analytics?.track('DeckEditorClickDeleteSlides', {
        deckName: deck.value?.title,
        deckId: deck.value?.id,
        slideType: slide.type,
        bulkAction: slides.length > 1,
      })
    })
  }
}

const tryDeleteDeck = async () => {
  deckEmbeds.value = await getEmbeds()
  return new Promise<void>(resolve => {
    const complete = () => deleteDeck().then(resolve)
    if (deckEmbeds.value.length) {
      Object.assign(deleteDeckWarningEmbeds, {
        showing: true,
        confirm: complete,
        cancel: () => (deleteDeckWarningEmbeds.showing = false),
      })
    } else {
      Object.assign(deleteDeckWarning, {
        showing: true,
        confirm: complete,
        cancel: () => (deleteDeckWarning.showing = false),
      })
    }
  })
}

const { contextMenu, open, close } = useContextMenu()

function onError(msg: unknown) {
  if (typeof msg === 'string') {
    notification.value = {
      title: 'An error occurred',
      text: msg,
      type: 'danger',
    }
  } else if (msg instanceof Error) {
    $sentry?.captureException(msg)
    notification.value = {
      title: 'An error occurred',
      text: 'Not all changes were saved successfully',
      type: 'danger',
    }
  } else {
    notification.value = null
  }
}

function onSlideLoading(loading: boolean) {
  isSlideLoading.value = loading
}

async function updateDeckInternalAccessNoNotification(value: boolean) {
  await updateDeck({ id: deck.value?.id, allow_internal_access: value })
  notification.value = null
}

async function insertSlides(slides: Array<Partial<Slide>> | Partial<Slide>, insertionIndex: number): Promise<void> {
  const index = insertionIndex < 0 ? 0 : insertionIndex
  const newSlides = await useEditDeckResult.insertSlides(slides, index)
  if (!newSlides) return
  setSelectedSlideIds(newSlides.every(s => s.type === 'S') ? newSlides.map(slide => slide.id) : [newSlides[0].id])

  $analytics?.track('DeckEditorAddASlideClickCreate', {
    deckName: deck.value?.title,
    deckId: deck.value?.id,
    slideType: Array.isArray(slides) ? slides[0].type : slides.type,
    bulkAction: Array.isArray(slides) && slides.length > 1,
  })
}

async function leaveEditAndResetScene() {
  await refreshDeck()
  useEditDeckResult.setCurrentSlideIndex(useEditDeckResult.currentSlideIndex.value) // refresh the slide (for site slides)
  const currentSlide = slides.value[useEditDeckResult.currentSlideIndex.value]
  threeDViewer.world.resetToSceneConfig()

  if (isThreeDSlide(currentSlide)) {
    threeDViewer.setSearch('')
    threeDViewer.setActiveScene(currentSlide.model_id, currentSlide.camera_id)
    threeDViewer.world.drillDataSetManager?.setMode('view')
  }

  $analytics?.track('DeckEditor3DSceneEditorClickCancel', {
    deckName: deck.value?.title,
    deckId: deck.value?.id,
    modelId: slide.value?.model_id,
  })

  leaveEditScene()
}

// TODO: We should look into refactoring this. There's quite a lot going
// on in here and the number of dependencies is causing it hard to extract
// this away.
function leaveEditScene() {
  threeDViewer.world.drillDataSetManager?.setMode('view')
  threeDEditMode.value = 'read'
  isEditingSite.value = false
  isNewSiteSlide.value = false
  isComposingNewScene.value = false
  isSavingAsScene.value = false
  isDuplicatingScene.value = false
  updateNewlyCreatedSlideId(null)
  unlockPage()
}

function finalizeSiteSlide() {
  if (isCreatingMultipleNewSiteSlides.value) return

  isEditingSite.value = false
  isNewSiteSlide.value = false
  leaveEditScene()
}

function activateNewSiteSlide() {
  isEditingSite.value = true
  isNewSiteSlide.value = true
  showAddSlideModal.value = null
}

function copyLinkToSlide(slideId: number) {
  if (deck.value) {
    copyToClipboard({ copyText: `${window.location.origin}/decks/${deck.value.id}?slide=${slideId}` })
    notification.value = { title: 'Copied To Clipboard', text: 'The link to the slide has been successfully copied' }
  } else {
    notification.value = { title: 'Whoops! Something went wrong', text: 'Please try again later', type: 'danger' }
  }
}

async function downloadThreeDSlide({ slideId, width, height }: { slideId: number; width: number | null; height: number | null }) {
  const slide = slides.value.find(s => s.id === slideId)
  try {
    if (!isThreeDSlide(slide) || !slide.camera_id) {
      throw new Error('Only 3D slides can be downloaded as an image using this method')
    }
    await threeDViewer.downloadImageFile(slide.model_id, width!, height!)
    notification.value = { title: 'Image downloaded' }
  } catch (e) {
    notification.value = { title: 'Whoops! Something went wrong', text: 'Please try again later', type: 'danger' }
  }
}

function multiSelectSlidesByType(type: SlideType) {
  if (!deck.value) return

  setSelectedSlideIds(slides.value.filter(slide => slide.type === type).map(x => x.id))
}

function setSelectedSlideIds(indices: number[]) {
  isEditingSite.value = false
  isNewSiteSlide.value = false
  _setSelectedSlideIds(indices)
}

async function handleArchiveDeck(): Promise<void> {
  await updateDeck({
    id: deck.value?.id,
    archived: !deck.value?.archived,
  })
}

const blurTitle = () => {
  updateNewlyCreatedSlideId(null)
}

async function copySlides(slides: Slide[]): Promise<void> {
  try {
    const order = useEditDeckResult.currentSlideIndex.value ?? 0
    const newSlides = await duplicateSlides(slides.map(s => ({ ...s, order })))

    setSelectedSlideIds([newSlides[0].id])
  } catch (e) {
    $sentry?.captureException(e)
    throw e
  }
}

async function handleUpdateDeckSettings(deckPayload: Partial<Deck>) {
  const shouldShowSecureAccessModal = deck.value?.view_access_type !== 'selected' && deckPayload.view_access_type === 'selected'

  if (shouldShowSecureAccessModal) {
    showDeckSettingsModal.value = false
    await updateDeck(deckPayload)
    showSecureAccessModal.value = true
  } else {
    showDeckSettingsModal.value = false
    await updateDeck(deckPayload)
  }
}

const next = () => {
  const index = filteredSlides.value.findIndex(slide => selectedSlideIds.value[0] === slide.id)
  const nextSlide = filteredSlides.value.slice(index + 1).find(isSelectableSlide)

  if (nextSlide) setSelectedSlideId(nextSlide.id)
}

const back = () => {
  const index = filteredSlides.value.findIndex(slide => selectedSlideIds.value[0] === slide.id)
  const prevSlide = pipe<[any], Slide[], DeepReadonly<Slide> | Slide | undefined>(
    slice(0, index),
    findLast(isSelectableSlide),
  )(filteredSlides.value)

  if (prevSlide) setSelectedSlideId(prevSlide.id)
}

const updateCaption = async (slide: Partial<Slide>) => {
  if (!slide.id) return

  try {
    await _updateCaption({ slideId: slide.id, newCaption: <string>slide.description })
  } catch (e) {
    notification.value = {
      title: 'An error occurred',
      text: 'Caption was not saved successfully',
      type: 'danger',
    }
    $sentry?.captureException(e)
    throw e
  }
}

const isBookMeetingEnabled = computed(() => userState.value.flags.BOOK_A_MEETING_ENABLED)

const hasAuthError = computed(() => error.value instanceof UseEditDeckAuthError)
const hasPermissionsError = computed(() => error.value instanceof UseEditDeckPermissionsError)
const needsPassword = computed(() => error.value instanceof UseEditDeckPasswordRequiredError)

const handleAddSlide = async (event: Partial<Slide>[]) => {
  await insertSlides(event, lastSelectedOrder.value + 1)
  showAddSlideModal.value = null
}

const handleAddThreeDSlide = async (event: { deck_id: number; type: 'M'; model_id: number }) => {
  const res = await threeDViewer.threeDRepo?.createNewCamera(event.model_id)
  if (res) {
    await handleAddSlide([{ ...event, title: res.title, camera_id: res.id }])
    isComposingNewScene.value = true
    threeDEditMode.value = 'write'
  }
}

const updateSlideTitleVisibility = async (slides: Slide[]) => {
  const makeVisible = !slides.some(s => s.title_visible_in_3d_model)
  await bulkUpdateSlides(slides.map(slide => ({ id: slide.id, title: slide.title, title_visible_in_3d_model: makeVisible })))
}

function onImportSlides({ slides, deckName }: { slides: Slide[]; deckName: string }) {
  showImportSlidesModal.value = false
  notification.value = {
    title: 'Slides Imported',
    text: `${slides.length} ${pluralize(slides.length, 'slide', 'slides')} successfully duplicated from ${deckName}`,
    type: 'success',
  }
}

function displayNotificationToast(notificationSpec: GlobalNotification | null) {
  notification.value = notificationSpec
}

async function handleToggleViewMode() {
  isGridView.value = !isGridView.value
}

useHead({
  title: () => (deck.value ? `${deck.value.title} - ${deck.value.company.name} | VRIFY` : 'VRIFY - Make Every Investor Interaction Count'),
})
</script>

<style lang="stylus" scoped>
.right-panel:empty
  display none
:deep(.priority-modals-only .v-multi-modal),
:deep(.priority-modals-only .v-modal:not(.priority-modal))
  display none
</style>
